import { useState, useEffect, useCallback } from 'react';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { ContentPermission } from 'types/channel';
import {
  getContentPermissions,
  updateContentPermissions,
  GetContentPermissionsResponse,
  UpdateContentPermissionsData,
} from '../api/marketplaceApi';

export interface UseContentPermissionsResult {
  permissions: ContentPermission | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  update: (data: UpdateContentPermissionsData) => Promise<boolean>;
  isUpdating: boolean;
}

export function useContentPermissions(
  contentType: string | null,
  contentId: string | null
): UseContentPermissionsResult {
  const apiClients = useApiClientsOrNull();

  const [permissions, setPermissions] = useState<ContentPermission | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);

  const refetch = useCallback(() => {
    setFetchTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!apiClients || !contentType || !contentId) {
      setPermissions(null);
      return;
    }

    let cancelled = false;

    const fetchPermissions = async () => {
      setLoading(true);
      setError(null);

      try {
        const response: GetContentPermissionsResponse = await getContentPermissions(
          apiClients.schemaClient,
          contentType,
          contentId
        );

        if (!cancelled) {
          setPermissions(response.permissions);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load permissions';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchPermissions();

    return () => {
      cancelled = true;
    };
  }, [apiClients, contentType, contentId, fetchTrigger]);

  const update = useCallback(
    async (data: UpdateContentPermissionsData): Promise<boolean> => {
      if (!apiClients || !contentType || !contentId) return false;

      setIsUpdating(true);
      setError(null);

      const previous = permissions;
      if (permissions) {
        setPermissions({ ...permissions, ...data });
      }

      try {
        const response = await updateContentPermissions(
          apiClients.schemaClient,
          contentType,
          contentId,
          data
        );
        setPermissions(response.permissions);
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update permissions';
        setError(message);
        setPermissions(previous);
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [apiClients, contentType, contentId, permissions]
  );

  return {
    permissions,
    loading,
    error,
    refetch,
    update,
    isUpdating,
  };
}
